import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import {
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  addWeeks,
  subWeeks,
  addMonths,
  subMonths,
  format,
  eachDayOfInterval,
  parseISO,
  differenceInMinutes,
  isWithinInterval
} from 'date-fns';

/**
 * Custom hook for timesheet data fetching and period management.
 *
 * @param {string} projectId - The project ID
 * @param {string} companyId - The company ID
 * @param {object} options - Configuration options
 * @param {boolean} options.enabled - Whether to fetch data
 * @returns {object} Timesheet data, period controls, and export methods
 */
export const useTimesheetData = (projectId, companyId, options = {}) => {
  const { enabled = true } = options;

  // Period state
  const [periodType, setPeriodType] = useState('week'); // 'week' | 'month' | 'custom'
  const [periodStart, setPeriodStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [periodEnd, setPeriodEnd] = useState(() => endOfWeek(new Date(), { weekStartsOn: 1 }));

  // Data state
  const [rawData, setRawData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter state
  const [departmentFilter, setDepartmentFilter] = useState(null);
  const [methodFilter, setMethodFilter] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Refs
  const loadingRef = useRef(false);
  const abortControllerRef = useRef(null);

  // Fetch timesheet data via RPC
  const fetchData = useCallback(async () => {
    if (!enabled || (!projectId && !companyId)) return;
    if (loadingRef.current) return;

    loadingRef.current = true;
    setLoading(true);
    setError(null);

    // Abort previous request
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();

    try {
      const { data, error: rpcError } = await supabase.rpc('get_timesheet_data', {
        p_project_id: projectId || null,
        p_company_id: companyId || null,
        p_start_date: format(periodStart, 'yyyy-MM-dd'),
        p_end_date: format(periodEnd, 'yyyy-MM-dd')
      });

      if (rpcError) throw rpcError;
      if (data?.error) throw new Error(data.error);

      setRawData(data);
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Error fetching timesheet data:', err);
        setError(err.message);
        toast.error('Failed to load timesheet data');
      }
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [projectId, companyId, periodStart, periodEnd, enabled]);

  // Fetch on mount and when dependencies change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Real-time subscription for check-in changes
  useEffect(() => {
    if (!projectId) return;

    const channel = supabase
      .channel('timesheet-checkins')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'site_checkins',
          filter: `project_id=eq.${projectId}`
        },
        () => {
          // Debounced refetch
          setTimeout(() => fetchData(), 500);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [projectId, fetchData]);

  // Days in current period
  const periodDays = useMemo(() => {
    return eachDayOfInterval({ start: periodStart, end: periodEnd });
  }, [periodStart, periodEnd]);

  // Process employees with computed totals
  const employees = useMemo(() => {
    if (!rawData?.employees) return [];

    return rawData.employees
      .map(emp => {
        // Group shifts by day
        const shiftsByDay = {};
        periodDays.forEach(day => {
          const dayStr = format(day, 'yyyy-MM-dd');
          shiftsByDay[dayStr] = [];
        });

        (emp.shifts || []).forEach(shift => {
          const dayStr = shift.shift_date;
          if (shiftsByDay[dayStr]) {
            shiftsByDay[dayStr].push(shift);
          }
        });

        // Calculate daily and total hours
        let totalGrossMinutes = 0;
        let totalPaidBreakMinutes = 0;
        let totalUnpaidBreakMinutes = 0;
        const methods = new Set();

        const dailySummary = periodDays.map(day => {
          const dayStr = format(day, 'yyyy-MM-dd');
          const dayShifts = shiftsByDay[dayStr] || [];

          let dayGross = 0;
          let dayPaidBreak = 0;
          let dayUnpaidBreak = 0;
          const dayMethods = new Set();
          let hasSelfie = false;

          dayShifts.forEach(shift => {
            dayGross += shift.gross_minutes || 0;
            dayMethods.add(shift.check_in_method || 'manual');
            if (shift.selfie_url) hasSelfie = true;

            (shift.breaks || []).forEach(brk => {
              const brkMins = brk.duration_minutes || 0;
              if (brk.is_paid) {
                dayPaidBreak += brkMins;
              } else {
                dayUnpaidBreak += brkMins;
              }
            });

            // Collect all methods
            dayMethods.forEach(m => methods.add(m));
          });

          totalGrossMinutes += dayGross;
          totalPaidBreakMinutes += dayPaidBreak;
          totalUnpaidBreakMinutes += dayUnpaidBreak;

          return {
            date: day,
            dateStr: dayStr,
            shifts: dayShifts,
            grossMinutes: dayGross,
            paidBreakMinutes: dayPaidBreak,
            unpaidBreakMinutes: dayUnpaidBreak,
            netMinutes: dayGross - dayUnpaidBreak,
            methods: Array.from(dayMethods),
            hasSelfie,
            hasShifts: dayShifts.length > 0,
            isComplete: dayShifts.length > 0 && dayShifts.every(s => s.check_out_time),
            isActive: dayShifts.some(s => !s.check_out_time)
          };
        });

        return {
          ...emp,
          dailySummary,
          totalGrossMinutes,
          totalPaidBreakMinutes,
          totalUnpaidBreakMinutes,
          totalNetMinutes: totalGrossMinutes - totalUnpaidBreakMinutes,
          methods: Array.from(methods)
        };
      })
      .filter(emp => {
        // Apply filters
        if (departmentFilter && emp.department !== departmentFilter) return false;
        if (methodFilter && !emp.methods.includes(methodFilter)) return false;
        if (searchQuery) {
          const q = searchQuery.toLowerCase();
          return emp.display_name?.toLowerCase().includes(q) ||
                 emp.email?.toLowerCase().includes(q);
        }
        return true;
      });
  }, [rawData, periodDays, departmentFilter, methodFilter, searchQuery]);

  // Departments from data
  const departments = useMemo(() => {
    if (!rawData?.employees) return [];
    const depts = new Set();
    rawData.employees.forEach(emp => {
      if (emp.department) depts.add(emp.department);
    });
    return Array.from(depts).sort();
  }, [rawData]);

  // Break types from data
  const breakTypes = useMemo(() => {
    return rawData?.break_types || [];
  }, [rawData]);

  // Period totals
  const periodTotals = useMemo(() => {
    return employees.reduce(
      (acc, emp) => ({
        grossMinutes: acc.grossMinutes + emp.totalGrossMinutes,
        paidBreakMinutes: acc.paidBreakMinutes + emp.totalPaidBreakMinutes,
        unpaidBreakMinutes: acc.unpaidBreakMinutes + emp.totalUnpaidBreakMinutes,
        netMinutes: acc.netMinutes + emp.totalNetMinutes,
        employeeCount: acc.employeeCount + 1
      }),
      { grossMinutes: 0, paidBreakMinutes: 0, unpaidBreakMinutes: 0, netMinutes: 0, employeeCount: 0 }
    );
  }, [employees]);

  // Period label
  const periodLabel = useMemo(() => {
    if (periodType === 'week') {
      return `${format(periodStart, 'MMM d')} - ${format(periodEnd, 'MMM d, yyyy')}`;
    }
    if (periodType === 'month') {
      return format(periodStart, 'MMMM yyyy');
    }
    return `${format(periodStart, 'MMM d')} - ${format(periodEnd, 'MMM d, yyyy')}`;
  }, [periodStart, periodEnd, periodType]);

  // Period navigation
  const goToNextPeriod = useCallback(() => {
    if (periodType === 'week') {
      const next = addWeeks(periodStart, 1);
      setPeriodStart(startOfWeek(next, { weekStartsOn: 1 }));
      setPeriodEnd(endOfWeek(next, { weekStartsOn: 1 }));
    } else if (periodType === 'month') {
      const next = addMonths(periodStart, 1);
      setPeriodStart(startOfMonth(next));
      setPeriodEnd(endOfMonth(next));
    }
  }, [periodType, periodStart]);

  const goToPrevPeriod = useCallback(() => {
    if (periodType === 'week') {
      const prev = subWeeks(periodStart, 1);
      setPeriodStart(startOfWeek(prev, { weekStartsOn: 1 }));
      setPeriodEnd(endOfWeek(prev, { weekStartsOn: 1 }));
    } else if (periodType === 'month') {
      const prev = subMonths(periodStart, 1);
      setPeriodStart(startOfMonth(prev));
      setPeriodEnd(endOfMonth(prev));
    }
  }, [periodType, periodStart]);

  const goToThisWeek = useCallback(() => {
    setPeriodType('week');
    setPeriodStart(startOfWeek(new Date(), { weekStartsOn: 1 }));
    setPeriodEnd(endOfWeek(new Date(), { weekStartsOn: 1 }));
  }, []);

  const goToLastWeek = useCallback(() => {
    const lastWeek = subWeeks(new Date(), 1);
    setPeriodType('week');
    setPeriodStart(startOfWeek(lastWeek, { weekStartsOn: 1 }));
    setPeriodEnd(endOfWeek(lastWeek, { weekStartsOn: 1 }));
  }, []);

  const goToThisMonth = useCallback(() => {
    setPeriodType('month');
    setPeriodStart(startOfMonth(new Date()));
    setPeriodEnd(endOfMonth(new Date()));
  }, []);

  const switchPeriodType = useCallback((type) => {
    setPeriodType(type);
    const now = new Date();
    if (type === 'week') {
      setPeriodStart(startOfWeek(now, { weekStartsOn: 1 }));
      setPeriodEnd(endOfWeek(now, { weekStartsOn: 1 }));
    } else if (type === 'month') {
      setPeriodStart(startOfMonth(now));
      setPeriodEnd(endOfMonth(now));
    }
  }, []);

  const setCustomRange = useCallback((start, end) => {
    setPeriodType('custom');
    setPeriodStart(start);
    setPeriodEnd(end);
  }, []);

  // Format minutes as "Xh Ym"
  const formatDuration = useCallback((minutes) => {
    if (!minutes || minutes <= 0) return '--';
    const h = Math.floor(minutes / 60);
    const m = Math.round(minutes % 60);
    if (h === 0) return `${m}m`;
    if (m === 0) return `${h}h`;
    return `${h}h ${m}m`;
  }, []);

  // Format minutes as decimal hours (e.g., 8.50)
  const formatDecimal = useCallback((minutes) => {
    if (!minutes || minutes <= 0) return '--';
    return (minutes / 60).toFixed(2);
  }, []);

  // Excel export
  const exportExcel = useCallback(async () => {
    try {
      const XLSX = await import('xlsx');

      const wb = XLSX.utils.book_new();

      // Sheet 1: Summary
      const summaryHeaders = ['Employee', 'Department', ...periodDays.map(d => format(d, 'EEE dd/MM')), 'Total Hours', 'Break Hours (Unpaid)', 'Net Hours'];
      const summaryRows = employees.map(emp => [
        emp.display_name,
        emp.department || '',
        ...emp.dailySummary.map(d => d.netMinutes > 0 ? formatDecimal(d.netMinutes) : ''),
        formatDecimal(emp.totalGrossMinutes),
        formatDecimal(emp.totalUnpaidBreakMinutes),
        formatDecimal(emp.totalNetMinutes)
      ]);
      summaryRows.push([
        'TOTALS', '',
        ...periodDays.map((_, i) => {
          const dayTotal = employees.reduce((sum, emp) => sum + (emp.dailySummary[i]?.netMinutes || 0), 0);
          return dayTotal > 0 ? formatDecimal(dayTotal) : '';
        }),
        formatDecimal(periodTotals.grossMinutes),
        formatDecimal(periodTotals.unpaidBreakMinutes),
        formatDecimal(periodTotals.netMinutes)
      ]);

      const wsSummary = XLSX.utils.aoa_to_sheet([summaryHeaders, ...summaryRows]);
      XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary');

      // Sheet 2: Daily Detail
      const detailHeaders = ['Employee', 'Date', 'Clock In', 'Clock Out', 'Method', 'Device', 'Gross Hours', 'Break (Unpaid)', 'Break (Paid)', 'Net Hours', 'Notes'];
      const detailRows = [];
      employees.forEach(emp => {
        emp.dailySummary.forEach(day => {
          day.shifts.forEach(shift => {
            const unpaidBreak = (shift.breaks || [])
              .filter(b => !b.is_paid)
              .reduce((sum, b) => sum + (b.duration_minutes || 0), 0);
            const paidBreak = (shift.breaks || [])
              .filter(b => b.is_paid)
              .reduce((sum, b) => sum + (b.duration_minutes || 0), 0);

            detailRows.push([
              emp.display_name,
              format(new Date(shift.check_in_time), 'dd/MM/yyyy'),
              format(new Date(shift.check_in_time), 'HH:mm'),
              shift.check_out_time ? format(new Date(shift.check_out_time), 'HH:mm') : 'Active',
              shift.check_in_method || 'manual',
              shift.device_name || '',
              formatDecimal(shift.gross_minutes),
              formatDecimal(unpaidBreak),
              formatDecimal(paidBreak),
              formatDecimal((shift.gross_minutes || 0) - unpaidBreak),
              [shift.check_in_notes, shift.check_out_notes].filter(Boolean).join(' | ')
            ]);
          });
        });
      });

      const wsDetail = XLSX.utils.aoa_to_sheet([detailHeaders, ...detailRows]);
      XLSX.utils.book_append_sheet(wb, wsDetail, 'Daily Detail');

      // Sheet 3: Breaks
      const breakHeaders = ['Employee', 'Date', 'Break Type', 'Paid', 'Start', 'End', 'Duration (mins)', 'Notes'];
      const breakRows = [];
      employees.forEach(emp => {
        emp.dailySummary.forEach(day => {
          day.shifts.forEach(shift => {
            (shift.breaks || []).forEach(brk => {
              breakRows.push([
                emp.display_name,
                format(new Date(brk.start_time), 'dd/MM/yyyy'),
                brk.break_type_name || 'Break',
                brk.is_paid ? 'Yes' : 'No',
                format(new Date(brk.start_time), 'HH:mm'),
                brk.end_time ? format(new Date(brk.end_time), 'HH:mm') : 'Active',
                brk.duration_minutes ? Number(brk.duration_minutes).toFixed(0) : '',
                brk.notes || ''
              ]);
            });
          });
        });
      });

      if (breakRows.length > 0) {
        const wsBreaks = XLSX.utils.aoa_to_sheet([breakHeaders, ...breakRows]);
        XLSX.utils.book_append_sheet(wb, wsBreaks, 'Breaks');
      }

      const filename = `Timesheet_${format(periodStart, 'yyyy-MM-dd')}_to_${format(periodEnd, 'yyyy-MM-dd')}.xlsx`;
      XLSX.writeFile(wb, filename);
      toast.success('Timesheet exported successfully');
      return true;
    } catch (err) {
      console.error('Error exporting timesheet:', err);
      toast.error('Failed to export timesheet');
      return false;
    }
  }, [employees, periodDays, periodStart, periodEnd, periodTotals, formatDecimal]);

  // Admin: add break to a shift
  const addBreak = useCallback(async (checkinId, userId, breakTypeId, startTime, endTime, isPaid, notes) => {
    try {
      const duration = endTime ? differenceInMinutes(new Date(endTime), new Date(startTime)) : null;

      const { data, error: insertError } = await supabase
        .from('shift_breaks')
        .insert({
          checkin_id: checkinId,
          user_id: userId,
          break_type_id: breakTypeId || null,
          is_paid: isPaid,
          start_time: startTime,
          end_time: endTime || null,
          duration_minutes: duration,
          notes: notes || null
        })
        .select()
        .single();

      if (insertError) throw insertError;

      toast.success('Break added');
      await fetchData();
      return data;
    } catch (err) {
      console.error('Error adding break:', err);
      toast.error('Failed to add break');
      return null;
    }
  }, [fetchData]);

  // Admin: update break
  const updateBreak = useCallback(async (breakId, updates) => {
    try {
      if (updates.end_time && updates.start_time) {
        updates.duration_minutes = differenceInMinutes(
          new Date(updates.end_time),
          new Date(updates.start_time)
        );
      }

      const { error: updateError } = await supabase
        .from('shift_breaks')
        .update(updates)
        .eq('id', breakId);

      if (updateError) throw updateError;

      toast.success('Break updated');
      await fetchData();
      return true;
    } catch (err) {
      console.error('Error updating break:', err);
      toast.error('Failed to update break');
      return false;
    }
  }, [fetchData]);

  // Admin: delete break
  const deleteBreak = useCallback(async (breakId) => {
    try {
      const { error: deleteError } = await supabase
        .from('shift_breaks')
        .delete()
        .eq('id', breakId);

      if (deleteError) throw deleteError;

      toast.success('Break deleted');
      await fetchData();
      return true;
    } catch (err) {
      console.error('Error deleting break:', err);
      toast.error('Failed to delete break');
      return false;
    }
  }, [fetchData]);

  return {
    // Data
    employees,
    departments,
    breakTypes,
    periodTotals,
    rawData,

    // Period
    periodType,
    periodStart,
    periodEnd,
    periodDays,
    periodLabel,

    // Period navigation
    goToNextPeriod,
    goToPrevPeriod,
    goToThisWeek,
    goToLastWeek,
    goToThisMonth,
    switchPeriodType,
    setCustomRange,

    // Filters
    departmentFilter,
    setDepartmentFilter,
    methodFilter,
    setMethodFilter,
    searchQuery,
    setSearchQuery,

    // State
    loading,
    error,

    // Actions
    refetch: fetchData,
    exportExcel,
    addBreak,
    updateBreak,
    deleteBreak,

    // Formatters
    formatDuration,
    formatDecimal
  };
};
